import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { amIReviewAdmin } from "@/utils/review.functions";

const links = [
  { to: "/admin/schedule/", label: "Schedule" },
  { to: "/admin/review/", label: "360 reviews" },
  { to: "/admin/demo/", label: "Demo topics" },
] as const;

/** Staff-only strip. Renders nothing unless the signed-in user has the admin role (checked on the server). */
export function AdminBar() {
  const checkAdmin = useServerFn(amIReviewAdmin);
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const evaluate = async (signedInEmail: string | null) => {
      if (!signedInEmail) return active && setEmail(null);
      try {
        const r = await checkAdmin();
        if (active) setEmail(r.admin ? signedInEmail : null);
      } catch {
        if (active) setEmail(null);
      }
    };
    supabase.auth.getSession().then(({ data }) => evaluate(data.session?.user.email ?? null));
    const { data } = supabase.auth.onAuthStateChange((_e, session) => {
      evaluate(session?.user.email ?? null);
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [checkAdmin]);

  if (!email) return null;

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth/", replace: true });
  };

  return (
    <div className="relative z-50 bg-primary text-primary-foreground">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-5 gap-y-1 px-6 py-2 text-[0.8125rem]">
        <span className="font-semibold uppercase tracking-wider">Staff</span>
        <nav aria-label="Admin" className="flex flex-wrap items-center gap-x-4">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="py-1 opacity-85 hover:opacity-100 hover:underline"
              activeProps={{ className: "underline opacity-100" }}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <span className="ml-auto hidden opacity-80 sm:inline">{email}</span>
        <button type="button" onClick={signOut} className="py-1 font-medium hover:underline">
          Sign out
        </button>
      </div>
    </div>
  );
}
